import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation, useSearchParams, Navigate } from 'react-router-dom';
import { FacebookLoginButton } from 'react-social-login-buttons';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { LoginSocialFacebook } from 'reactjs-social-login';
import { useCookieConsentContext } from '@use-cookie-consent/react';
import { useTranslation } from 'react-i18next';
import { AccountCircle as AccountCircleIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { Button, Divider, FormControl, Grid, IconButton, InputAdornment, InputLabel, OutlinedInput, Stack, Typography } from '@mui/material';
import { useLoginMutation, useLoginWithGoogleJWTMutation, useLoginWithFacebookMutation } from '../features/api/rest/apiAuthSlice';
import { useGetProfileQuery } from '../features/api/rest/apiProfileSlice';
import CookiePreferenceButton from './Layout/Cookies/CookiePreferenceButton';
import { openCookiePreferences as openCookiePreferencesAction, setAlertMessage } from '../features/layoutSlice';
import Spinner from './Spinner';
import config from '../app/config';

function Login() {
  const { t } = useTranslation();
  const [user, setUser] = useState('');
  const [pwd, setPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [searchParams] = useSearchParams();
  const { consent } = useCookieConsentContext();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const goToNext = () => {
    const next = searchParams.get('next');
    if (next !== null) {
      window.location.replace(next);
    } else {
      navigate(location.state?.next || '/');
    }
  };

  const goToNextComponent = () => {
    const next = searchParams.get('next');
    if (next !== null) {
      window.location.replace(next);
    } else {
      return <Navigate to={location.state?.next || '/'} />;
    }
    return null;
  };

  const [login] = useLoginMutation();
  const [loginWithGoogleJWT] = useLoginWithGoogleJWTMutation();
  const [loginWithFacebook] = useLoginWithFacebookMutation();

  const openCookiePreferences = () => dispatch(openCookiePreferencesAction());

  const handleUserChange = ({ target }) => {
    setUser(target.value);
  };
  const handlePwdChange = ({ target }) => {
    setPwd(target.value);
  };
  const handleClickShowPassword = () => {
    setShowPwd((prevShowPwd) => !prevShowPwd);
  };
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await login({ username: user, password: pwd }).unwrap();
      setUser('');
      setPwd('');
      dispatch(setAlertMessage({ text: t('auth.messages.login.success'), type: 'success' }));
      return goToNext();
    } catch (err) {
      dispatch(setAlertMessage({ text: t('auth.messages.login.error'), type: 'error' }));
      return navigate(location, { state: { ...location.state } });
    }
  };

  const { currentData, isLoading, isFetching, status, isSuccess } = useGetProfileQuery();

  const handleFacebookLoginSuccess = async (response) => {
    try {
      await loginWithFacebook({ accessToken: response.data.accessToken }).unwrap();
      dispatch(setAlertMessage({ text: t('auth.messages.facebookLogin.success'), type: 'success' }));
      goToNext();
    } catch (err) {
      navigate(location, { state: { ...location.state } });
      dispatch(setAlertMessage({ text: t('auth.messages.facebookLogin.error'), type: 'error' }));
    }
  };

  const handleError = (err) => {
    console.error(err);
  };

  const handleGoogleLoginSuccess = async (response) => {
    try {
      await loginWithGoogleJWT({ accessToken: response.credential }).unwrap();
      goToNext();
    } catch (err) {
      navigate(location, { state: { ...location.state } });
      dispatch(setAlertMessage({ text: t('auth.messages.googleLogin.error'), type: 'error' }));
    }
  };

  const facebookButton = (
    <Stack justifyContent="center" alignItems="center">
      <LoginSocialFacebook appId={config.api.facebook.clientid} onResolve={handleFacebookLoginSuccess} onReject={handleError}>
        <FacebookLoginButton style={{ width: '300px' }}>{t('auth.loginForm.fields.login.facebook')}</FacebookLoginButton>
      </LoginSocialFacebook>
    </Stack>
  );

  const googleButton = (
    <Stack justifyContent="center" alignItems="center">
      <GoogleOAuthProvider clientId={config.api.google.clientid}>
        <GoogleLogin width="300px" onSuccess={handleGoogleLoginSuccess} onError={handleError} />
      </GoogleOAuthProvider>
    </Stack>
  );

  if (isLoading || isFetching) return <Spinner />;

  if (status === 'fulfilled' && currentData && isSuccess) return goToNextComponent();

  return (
    <>
      <Stack component="section" id="Login" data-testid="Login" direction="column" spacing={2} sx={{ mt: 4 }}>
        <Typography variant="h1" align="center" component="h1" sx={{ mb: 2 }}>
          {t('auth.loginForm.heading')}
        </Typography>

        <Stack component="form" direction="column" alignItems="center" onSubmit={handleSubmit}>
          <FormControl sx={{ mb: 1, width: '100%' }} variant="outlined">
            <InputLabel htmlFor="username">{t('auth.loginForm.fields.username.label')}</InputLabel>
            <OutlinedInput
              id="username"
              data-testid="username"
              type="text"
              required
              value={user}
              onChange={handleUserChange}
              startAdornment={
                <InputAdornment position="start">
                  <AccountCircleIcon />
                </InputAdornment>
              }
              label={t('auth.loginForm.fields.username.label')}
            />
          </FormControl>

          <FormControl sx={{ mt: 1, width: '100%' }} variant="outlined">
            <InputLabel htmlFor="password">{t('auth.loginForm.fields.password.label')}</InputLabel>
            <OutlinedInput
              id="password"
              data-testid="password"
              type={showPwd ? 'text' : 'password'}
              required
              value={pwd}
              onChange={handlePwdChange}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton aria-label={t('aria.icons.togglePasswordVisibility')} onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                    {showPwd ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              }
              label={t('auth.loginForm.fields.password.label')}
            />
          </FormControl>

          <Grid>
            <Button type="submit" data-testid="submit" color="secondary" variant="contained" sx={{ p: 1, mt: 2, mb: 1 }}>
              {t('auth.loginForm.fields.login.label')}
            </Button>
            <Button type="button" color="secondary" variant="contained" sx={{ p: 1, mt: 2, ml: 1, mb: 1 }} onClick={() => navigate('/registration')}>
              {t('auth.loginForm.fields.signup.label')}
            </Button>
          </Grid>
          <Divider>{t('auth.loginForm.separator')}</Divider>
          <Button type="button" color="secondary" variant="contained" sx={{ p: 1, mt: 1 }} onClick={() => navigate('/reset/password')}>
            {t('auth.loginForm.fields.forgotPassword.label')}
          </Button>
        </Stack>

        <Divider>{t('auth.loginForm.separator')}</Divider>

        {config.socialLogin.facebook && facebookButton}
        {config.socialLogin.google && consent.thirdParty && googleButton}
      </Stack>
      <CookiePreferenceButton onClick={openCookiePreferences} />
    </>
  );
}

export default Login;
